import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";

const empty = { name: "", email: "", title: "", linkedin: "", expertise: "", qualifications: "", bio: "" };

const Input = ({ label, k, form, setForm, textarea, rows = 4, ...rest }) => (
  <label className="block mb-4">
    <span className="block text-sm font-semibold mb-1">{label}</span>
    {textarea ? (
      <textarea rows={rows} className="field" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`faculty-apply-${k}`} {...rest}/>
    ) : (
      <input className="field" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`faculty-apply-${k}`} {...rest}/>
    )}
  </label>
);

export default function FacultyApplyPage() {
  useSeo({ title: "Apply to join the CSI faculty", description: "Apply to teach and examine with the Converged Security Institute.", path: "/join-faculty" });
  const [form, setForm] = useState(empty);
  const [state, setState] = useState({ busy: false, err: "", done: false });

  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, err: "", done: false });
    try {
      await api.post("/faculty-applications", form);
      setState({ busy: false, err: "", done: true });
    } catch (ex) {
      setState({ busy: false, err: formatApiError(ex.response?.data?.detail) || ex.message, done: false });
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-10" data-testid="faculty-apply-page">
      <div className="eyebrow mb-2">Faculty</div>
      <h1 className="font-display text-3xl sm:text-4xl">Apply to join the CSI faculty</h1>
      <p className="text-sm text-zinc-600 mt-3">
        Applications are reviewed by the CSI Admin team. Your profile appears on the website and instructor access is enabled only after approval.
      </p>
      {state.done ? (
        <div className="card-warm p-6 mt-6" data-testid="faculty-apply-success">
          <h2 className="font-display text-2xl">Application received</h2>
          <p className="text-sm text-zinc-700 mt-2">Thank you. We have emailed you a confirmation and will reply once your application has been reviewed.</p>
          <Link to="/instructors" className="chip inline-flex mt-4">Back to faculty</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="card-warm p-6 mt-6">
          <div className="grid sm:grid-cols-2 gap-x-4">
            <Input label="Full name" k="name" form={form} setForm={setForm} required minLength={2}/>
            <Input label="Email" k="email" form={form} setForm={setForm} type="email" required/>
            <Input label="Professional title" k="title" form={form} setForm={setForm} required placeholder="e.g. Senior Lecturer in OT Security"/>
            <Input label="LinkedIn (optional)" k="linkedin" form={form} setForm={setForm} placeholder="https://www.linkedin.com/in/…"/>
          </div>
          <Input label="Areas of expertise" k="expertise" form={form} setForm={setForm} required placeholder="e.g. Governance, OT/ICS, incident response"/>
          <Input label="Qualifications (one per line)" k="qualifications" form={form} setForm={setForm} textarea rows={3}/>
          <Input label="Short biography" k="bio" form={form} setForm={setForm} textarea rows={6} required minLength={30}/>
          {state.err && <div className="text-sm text-red-600 mb-3" data-testid="faculty-apply-error">{state.err}</div>}
          <button disabled={state.busy} className="btn-terracotta w-full py-3 rounded-full text-sm font-semibold disabled:opacity-60" data-testid="faculty-apply-submit">
            {state.busy ? "Sending…" : "Submit application"}
          </button>
        </form>
      )}
    </div>
  );
}
